import { Backdrop, CircularProgress, createTheme, Grid, IconButton, MuiThemeProvider, Typography } from '@material-ui/core';
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import CheckboxUI from '../../../components/Form/Checkbox';
import Select from '../../../components/Form/Select';
import SelectRackType from '../../../components/Form/SelectRackType';
import SelectSupplier from '../../../components/Form/SelectSupplier';
import TextField from '../../../components/Form/TextField';
import AlertDialog from '../../../components/ui/AlertDialog';
import ButtonUI from '../../../components/ui/ButtonUI';
import { useAuth } from '../../../hooks/auth';
import MvmApi from '../../../services/MvmApi';
import ShedApi from '../../../services/ShedApi';
import SupplierApi from '../../../services/SupplierApi';
import getDefaultGridOptions, { defaultHeaderOptions } from '../../../utils/componentUtils';
import { COMPANIES_WITH_FCM_RACKS, COMPANY_LIST, MVM_RACKLOAD_TYPE, MVM_RACKLOAD_TYPE_FIASA } from '../../../utils/constants';
import { useStyles } from '../Style';

type props = {
  control: any,
  title?: string,
  name: string,
  allowInvoiceEdit?: boolean,
  allowAdd: boolean,
  allowDelete?: boolean,
  allowRequestNumberEdit?: boolean,
  showColumns: any,
  shedId?: any,
  movementTypeId?: any,
  movementTypeDescription?: any,
  defaultSupplier?: any,
  rackTypes?: any
};

interface IFormInput {
  requestNumber: any;
  supplier: any;
  indFullRack: any;
  rackType: any;
  rackTypeQuantity: any;
  volumeQuantity: any;
  shippingCompany: any;
  groupDrop: any;
  selectedCFOP: any;
  invoiceCFOP: any;
  rackTypeText: any;
}

type loadGroup = {
  groupIndex: number;
  items: any[];
}

type loadGroupOptions = {
  code: number;
  label: string;
}

//allowInvoiceEdit: boolean - Indica se deve exibir a coluna para editar NF de saida
//showColumns: array - define quais colunas adicionais devem ser exibidas na grid.
const MvmRackLoadList: React.FC<props> = ({ control, title, name, shedId, movementTypeId, allowInvoiceEdit, allowRequestNumberEdit, allowAdd, allowDelete, defaultSupplier, showColumns, rackTypes }) => {
  const { user, isPortoReal, isJeep, isFiasa } = useAuth();
  const classes = useStyles();
  const { t } = useTranslation("Mvm");
  const { t: _t } = useTranslation("General");
  const { t: tval } = useTranslation("Validation");


  const { t: it } = useTranslation("MvmRackLoad");
  const { fields, update, append, remove } = useFieldArray({ control, name: name });
  const refRackTypeField = useRef<HTMLElement>(null)
  const refInvoiceCFOPField = useRef<HTMLElement>(null)
  const [circularLoading, setCircularLoading] = useState(false)
  const [shed, setShed] = useState<any>({})
  const [shedCompanyList, setShedCompanyList] = useState<any>({});
  const [movementTypeDescription, setMovementTypeDescription] = useState("")
  const [groupOptions, setGroupOptions] = useState<loadGroupOptions[]>([{ code: 0, label: "Novo Grupo" }])
  const [loadGroups, setLoadGroups] = useState<loadGroup[]>([])
  const [expandRows, setExpandRows] = useState<number[]>([])
  const { enqueueSnackbar } = useSnackbar()
  const { control: formGridControl, setError: setGridError, clearErrors: clearGridErrors } = useForm({})
  const { control: formControl, handleSubmit, reset, setError, clearErrors, setValue, getValues, watch } = useForm<IFormInput>({
    defaultValues: {
      requestNumber: '',
      supplier: {},
      indFullRack: false,
      rackType: {},
      rackTypeQuantity: '',
      volumeQuantity: '',
      shippingCompany: {},
      groupDrop: {},
      selectedCFOP: {},
      invoiceCFOP: '',
      rackTypeText: ''
    }
  })

  const myTheme = createTheme({
    overrides: {
      MuiTableBody: {
        root: {
          cursor: ''
        }
      }
    }
  })

  const watchInvoiceCFOP = watch("selectedCFOP", {});

  // Carregar os rackTypes na lista, caso o Mvm ja tenha uma lista cadastrada no banco (proveniente do logistics)
  useEffect(() => {
    if (!rackTypes || !shed || !movementTypeDescription)
      return;

    // Caso o effect dispare novamente, evitar readicionar tudo na lista
    if (fields.length > 0)
      return;

    if (rackTypes?.length === 0)
      return;

    const loadExistingRackTypes = async () => {
      // Necessario buscar os fornecedores de cada rackType só pra pegar o cod SAP, que vai ser usado na api ao salvar tudo:
      const suppSearch: any[] = (await SupplierApi.searchSuppliers(rackTypes.map((x: any) => x.supplierId), user.company.plantID))?.items;

      rackTypes.forEach((item: any, index: number) => {

        const supplierSAP = suppSearch.find((x) => x.supplierID === item.supplierId)?.supplierSAP;

        const addRackType = {
          ...item,
          originalId: item.id, //guardar este id para as operacoes do fieldArray nao sobrescrever
          typeReqNumberInGrid: true, //se a embalagem for fcm, user ira digitar o reqnumber na grid
          requestNumber: item.requestNumber ?? "",
          selectedCFOP: item.invoiceCFOP,
          shippingCompany: { code: item.shippingCompanyId },
          indFullRack: false,
          rackType: {
            code: item.rackTypeCode,
            rackTypeId: item.rackTypeId,
            rackTypeCode: item.rackTypeCode,
            rackTypeDescription: item.rackTypeDescription,
            ownerSupplierId: item.rackTypeOwnerSupplierId
          },
          rackTypeText: item.rackTypeDescription,
          supplier: {
            code: item.supplierId,
            name: item.supplierName,
            supplierUf: item.supplierUF,
            sapCode: supplierSAP,
          }
        }

        // Para o caso da embalagem for FCM, ja exibir um alerta
        if (addRackType.requestNumber === "")
          setGridError("requestNumber_" + index, { message: tval('requiredFieldForPackingFCM') });

        addItemToList(addRackType, false);
      });
    };

    loadExistingRackTypes();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rackTypes, shed, movementTypeDescription]);

  useEffect(() => {
    setValue('invoiceCFOP', watchInvoiceCFOP?.code)  //falta passar o invoiceCFOP com string somente o código
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchInvoiceCFOP]);

  useEffect(() => {

    //Buscar dados do Shed passado para este componente
    if (shedId)
      ShedApi.get(shedId).then((response) => {
        if (response) {
          setShed(response);

          //company List: Para o <Select> ShippingCompany
          let companyList = response.companies.map((x: any) => ({ code: x.companyId, label: x.companyName, uf: x.companyUF, companyCode: x.companyCode }));

          setShedCompanyList(companyList);

          //...se houver apenas 1 company, deixar esta selecionada
          if (companyList.length === 1)
            setValue('shippingCompany', companyList[0]);

          //movementTypes: para o <Select> de movement Type
          let movType: any = response.movementTypes.filter((x: any) => Number(x.movementTypeId) === Number(movementTypeId));
          setMovementTypeDescription(movType[0].movementType.description);
        }
      });


    //Validar Request Numbers: somente se a pagina exigir esse campo,
    // validar cada item da lista para ja setar se o item tem erro ou nao
    const validateRequestNumbers = async () => {
      for (let i = 0; i < fields.length; i++) {
        let rackLoad: any = fields[i];
        if (!await yupRequestNumberValidation(rackLoad.requestNumber, i)) {
          rackLoad.hasError = true;
          update(i, rackLoad);
        }
      }
    }

    if (allowRequestNumberEdit)
      validateRequestNumbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shedId]);

  useEffect(() => {
    //Campo Fornecedor pre carregado com o mesmo fornecedor do MVM:
    if (defaultSupplier) {
      setValue("supplier", defaultSupplier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shed])

  useEffect(() => {
    if (loadGroups.length === 0)
      return
    const rows = loadGroups.map(el => el.groupIndex - 1)
    setExpandRows(rows)
  }, [loadGroups])

  const columnsGroup = [
    {
      name: "groupIndex",
      label: "Carregamentos Agrupados",
      ...defaultHeaderOptions,
    }
  ]

  const columns = [
    {
      name: 'id',
      options: { display: false }
    },
    {
      name: 'shedMovementTypeDescription',
      options: { display: false }
    },
    {
      name: 'invoiceCFOP',
      options: { display: false }
    },
    {
      name: 'supplierId',
      options: { display: false }
    },
    {
      name: 'rackTypeCode',
      options: { display: false }
    },
    {
      name: 'requestNumber',
      options: { display: false }
    },
    {
      name: 'shippingCompanyId',
      options: { display: false }
    },
    {
      name: 'invoiceGroup',
      options: { display: false }
    },
    {
      name: 'typeReqNumberInGrid',
      options: { display: false }
    },
    {
      name: 'shedCode',
      label: String(it('dock')),
      options: {
        ...defaultHeaderOptions,
        display: showColumns.indexOf('shedCode') >= 0,
        customBodyRender: (value: any, tableMeta: any) => (value + ' - ' + tableMeta.rowData[1])
      }
    },
    //não sei o motivo desse campo existir aqui,
    //{
    //    name: 'typeCFOP',
    //    label: it('typeCFOP'),
    //    options: {
    //        ...defaultHeaderOptions,
    //        display: //showColumns.indexOf('typeCFOP') >= 0,
    //        customBodyRender: (value: any, tableMeta: any) => ( 
    //            getCFOPType(tableMeta.rowData[2])
    //        )
    //    }
    //},
    {
      name: !allowAdd ? 'invoiceCFOP' : 'selectedCFOP',
      label: it('typeCFOP'),
      options: {
        ...defaultHeaderOptions,
        //display: user.company.companyID == '167',
        customBodyRender: (value: any, tableMeta: any) => (
          <div>{!allowAdd ? (user.company.companyID === '167' ? MVM_RACKLOAD_TYPE.find(e => e.code === value)?.label : MVM_RACKLOAD_TYPE_FIASA.find(e => e.code === value)?.label) : value?.label}</div>
        )
      }
    },
    {
      name: 'requestNumber',
      label: user.company.companyID === '167' ? it('RequestNumber164') : String(showColumns.indexOf('requestNumber') >= 0 ? it('requestNumber') : it('transportOrder')),
      options: {
        ...defaultHeaderOptions,
        display: (allowRequestNumberEdit || showColumns.indexOf('requestNumber') >= 0 || showColumns.indexOf('transportOrder') >= 0),
        customBodyRender: (value: any, tableMeta: any) => {
          const typeReqNumberInGrid = tableMeta.rowData[8]; // Indica se deve digitar o campo na grid, pois ele foi carregado do logistics
          return allowRequestNumberEdit || typeReqNumberInGrid ? renderRequestNumberField(value, tableMeta) : value;
        }
      }
    },
    {
      name: 'invoiceNumber',
      label: it('invoiceNumber'),
      options: {
        ...defaultHeaderOptions,
        display: showColumns.indexOf('invoiceNumber') >= 0
      }
    },
    {
      name: 'indFullRack',
      label: String(it('indFullRack')),
      options: {
        ...defaultHeaderOptions,
        display: user.company.companyID === '167' ? false : (showColumns.indexOf('indFullRack') >= 0),
        customBodyRender: (value: any) => (value ? _t("yes") : _t("no"))
      }
    },
    {
      name: 'rackTypeDescription',
      label: String(it('rackTypeDescription')),
      options: {
        ...defaultHeaderOptions,
        customBodyRender: (value: any, tableMeta: any) => {
          return tableMeta.rowData[4] + (value !== '' ? (' - ' + value) : '')
        }
      }
    },

    {
      name: 'supplierName',
      label: String(_t('supplier')),
      options: {
        ...defaultHeaderOptions,
        sort: false,
        customBodyRender: (value: any, tableMeta: any) => (tableMeta.rowData[3] + ' - ' + value)
      }
    },
    {
      name: 'rackTypeQuantity',
      label: String(it('rackTypeQuantity')),
      options: {
        ...defaultHeaderOptions,
        sort: false
      }
    },
    {
      name: 'volumeQuantity',
      label: String(it('rackTypeVolume')),
      options: {
        ...defaultHeaderOptions,
        sort: false,
        display: showColumns.indexOf('rackTypeVolume') >= 0
      }
    },
    {
      name: 'shippingCompanyId',
      label: String(it('company')),
      options: {
        ...defaultHeaderOptions,
        sort: false,
        display: showColumns.indexOf('company') >= 0,
        customBodyRender: (value: any, tableMeta: any) => (COMPANY_LIST.filter(x => x.code === String(value))[0].label)
      }
    },
    {
      name: 'invoiceKey',
      label: String(it('invoiceKey')),
      options: {
        ...defaultHeaderOptions,
        display: allowInvoiceEdit,
        sort: false,
        customBodyRender: (value: any, tableMeta: any) => (
          renderInvoiceField(value, tableMeta)
        )
      }
    },
    {
      name: 'invoiceNumber',
      label: String(it('invoiceNumber')),
      options: {
        ...defaultHeaderOptions,
        display: allowInvoiceEdit,
        sort: false
      }
    },
    {
      name: 'rackDispatchReturnMessage',
      label: String(it('rackDispatchReturnMessage')),
      options: {
        ...defaultHeaderOptions,
        sort: false,
        display: showColumns.indexOf('rackDispatchReturnMessage') >= 0
      }
    },
    {
      name: 'id',
      label: ' ',
      options: {
        display: allowDelete,
        setCellProps: () => ({
          align: "right"
        }),
        disableClick: true,
        sort: false,
        customBodyRender: (value: any, tableMeta: any) => (
          renderButtons(value)
        )
      }
    }
  ]

  //Validacao sem uso de Form: personalizado para exibir no campo invoiceKey_x
  const yupInvoiceValidation = async (data: any, rowIndex: number) => {
    clearGridErrors("invoiceKey_" + rowIndex);

    try {
      const schema = Yup.string().nullable()
        .matches(/\b\d{44}\b/, { message: tval("requiredLength44"), excludeEmptyString: true }); //Null OU exatamente 44 numeros

      await schema.validate(data, {
        abortEarly: true
      });
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError)
        setGridError("invoiceKey_" + rowIndex, { message: err.message });
      return false;
    }
  }

  //Validacao sem uso de Form: personalizado para exibir no campo requestNumber_x
  const yupRequestNumberValidation = async (data: any, rowIndex: number) => {
    clearGridErrors("requestNumber_" + rowIndex);

    try {
      const schema = Yup.string().required(tval("requiredField")).max(12, tval("maxLength12"));

      await schema.validate(data, {
        abortEarly: true
      });
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError)
        setGridError("requestNumber_" + rowIndex, { message: err.message });
      return false;
    }
  }

  const yupValidation = async (data: any) => {
    clearErrors(["selectedCFOP", "requestNumber", "supplier", "indFullRack", "rackType", "rackTypeText", "rackTypeQuantity", "volumeQuantity", "shippingCompany"]);

    try {
      const schema = Yup.object({
        isRackFCM: Yup.boolean(),
        //RequestNumber: condicional: somente required se for Embalagem FCM e não Embalagem cheia

        requestNumber: watchInvoiceCFOP?.code === '9999' ? Yup.string().required(tval("requiredField")) :
          Yup.string().nullable().when(['isRackFCM', 'indFullRack', 'typeReqNumberInGrid'], {
            is: (isRackFCM: boolean, indFullRack: boolean, typeReqNumberInGrid: boolean) => isRackFCM && !indFullRack && !typeReqNumberInGrid,
            then: (schema) => schema.required(tval('requiredFieldForPackingFCM')),
            otherwise: (schema) => schema.nullable()
          }),
        selectedCFOP: !isJeep() ? Yup.object().nullable() : Yup.object().nullable()
          .required(tval("requiredField"))
          .shape({ code: Yup.string().required(tval("requiredField")) }),
        shippingCompany: Yup.object().nullable()
          .required(tval("requiredField"))
          .shape({ code: Yup.string().required(tval("requiredField")) }),
        supplier: Yup.object().nullable()
          .required(tval("requiredField"))
          .shape({ code: Yup.string().required(tval("requiredField")) }),
        indFullRack: isPortoReal() || isJeep() ? Yup.boolean().nullable() : Yup.boolean().required(tval("requiredField")),
        rackType: Yup.object().nullable()
          .required(tval("requiredField"))
          .shape({ code: Yup.string().required(tval("requiredField")) }),
        //rackTypeText: !isPortoReal() ? Yup.string().nullable() :
        // Yup.string().nullable()
        //    .required(tval("requiredField")),
        rackTypeQuantity: Yup.number()
          .required(tval("requiredField"))
          .positive(tval("mustBeGreaterThan0"))
          .typeError(tval("mustBeNumber")),
        volumeQuantity: Yup.number()
          .required(tval("requiredField"))
          .positive(tval("mustBeGreaterThan0"))
          .typeError(tval("mustBeNumber")),
      })

      await schema.validate(data, {
        abortEarly: false,
      })

    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach(error => {
          setError(error.path?.split('.')[0] as "requestNumber" | "supplier" | "indFullRack" | "rackType" | "rackTypeQuantity" | "volumeQuantity" | "shippingCompany" | "selectedCFOP", { message: error.message })
        })
      }
      return false;
    }

    return true;
  }

  const renderInvoiceField = (value: any, tableMeta: any) => {
    let cfopNumber = tableMeta?.currentTableData[tableMeta.rowIndex]?.data[2];
    let nextCfopNumber = tableMeta?.currentTableData[tableMeta.rowIndex + 1]?.data[2];

    let supplierNumber = tableMeta?.currentTableData[tableMeta.rowIndex]?.data[3];
    let nextSupplierNumber = tableMeta?.currentTableData[tableMeta.rowIndex + 1]?.data[3];

    let requestNumber = tableMeta?.currentTableData[tableMeta.rowIndex]?.data[5];
    let nextRequestNumber = tableMeta?.currentTableData[tableMeta.rowIndex + 1]?.data[5];

    let shippingCompanyId = tableMeta?.currentTableData[tableMeta.rowIndex]?.data[6];
    let nextShippingCompanyId = tableMeta?.currentTableData[tableMeta.rowIndex + 1]?.data[6];

    let invoiceGroup = tableMeta?.currentTableData[tableMeta.rowIndex]?.data[7];
    let nextInvoiceGroup = tableMeta?.currentTableData[tableMeta.rowIndex + 1]?.data[7];

    return (
      // Número da requisição diferente do número da próxima linha
      (requestNumber === nextRequestNumber
        && cfopNumber === nextCfopNumber
        && supplierNumber === nextSupplierNumber
        && shippingCompanyId === nextShippingCompanyId
        && invoiceGroup === nextInvoiceGroup)
        ?
        <></>
        :
        <TextField
          name={`invoiceKey_${tableMeta.rowIndex}`}
          key={`invoiceKey_${tableMeta.rowIndex}`}
          control={formGridControl}
          onChange={(e) => e.target.value?.length === 44 && invoiceChange(tableMeta.rowIndex, e.target.value)}
          inputProps={{ maxLength: 44 }}
          defaultValue={value}
          fullWidth />
      //: 
      //    <></>
    );
  }

  const renderRequestNumberField = (value: any, tableMeta: any) => {
    return (
      <TextField
        name={`requestNumber_${tableMeta.rowIndex}`}
        key={`requestNumber_${tableMeta.rowIndex}`}
        control={formGridControl}
        onChange={(e) => requestNumberChange(tableMeta.rowIndex, e.target.value)}
        inputProps={{ maxLength: 12 }}
        defaultValue={value}
        fullWidth />
    );
  }

  const renderButtons = (id: any) => {

    let mvmShedId = 0
    let rowIndex = -1

    for (let i = 0; i < fields.length; i++) {
      if (fields[i].id === id) {
        mvmShedId = (fields as any)[i].mvmShedId
        rowIndex = i
        break
      }
    }

    if (rowIndex < 0)
      return <></>

    //Se usuario acabou de incluir este item na lista, ela tem mvmShedId=0
    let justAdded = mvmShedId === 0;

    if ((allowAdd && justAdded) || allowDelete)
      return (
        <AlertDialog
          dialogTitle={_t("dialogDeleteTitle")}
          dialogContentText={_t("dialogDeleteContentText")}
          cancelTextButton={_t("cancelDeleteTextButton")}
          confirmTextButton={_t("confirmDeleteTextButton")}
          confirmDialogFunction={() => { removeItemFromList(rowIndex) }}
          Button={
            <IconButton size='small' edge="end">
              <DeleteIcon />
            </IconButton>
          }
        />
      );
    else
      return (<></>)
  }

  //Atualiza o valor da nota fiscal (id = indice do array de fields, enviado para a funcao update do useFieldArray)
  const invoiceChange = async (id: any, value: any) => {
    let item: any = fields[id];

    item.hasError = !(await yupInvoiceValidation(value, id));

    let invoiceNumber = Number(value.substring(25, 34)).toString();
    if (invoiceNumber === '0') invoiceNumber = '';

    item.invoiceKey = value;
    item.invoiceNumber = invoiceNumber;

    update(id, item);
  }

  //Atualiza o valor da requisicao (id = indice do array de fields, enviado para a funcao update do useFieldArray)
  const requestNumberChange = async (id: any, value: any) => {
    let item: any = fields[id];

    // Atualizar o fieldArray cria um id string aleatorio. Mas se o item ja tiver um id do banco, 
    // sera necessario preservar o mesmo para quando submeter pra api
    item.id = item.originalId ?? item.id;

    item.hasError = !(await yupRequestNumberValidation(value, id));
    item.requestNumber = value;

    update(id, item);
  }

  const proccessItemGroupInfo = (item: any, groupIndex: number) => {
    const nextGroup = loadGroups.length > 0 ? loadGroups[loadGroups.length - 1].groupIndex + 1 : 1
    item.invoiceGroup = !groupIndex || groupIndex === 0 ? nextGroup : groupIndex
  }

  const proccessGroupList = () => {
    let groupOptionstmp: loadGroupOptions[] = [{ code: 0, label: "Novo Grupo" }]
    let loadGroupstmp: loadGroup[] = []

    for (let i = 0; i < fields.length; i++) {

      let groupIndex = (fields[i] as any).invoiceGroup
      if (groupIndex === 0)
        groupIndex = 1

      if (groupOptionstmp.filter(el => el.code === groupIndex).length === 0)
        groupOptionstmp.push({ code: groupIndex, label: `Grupo: ${groupIndex}` })

      if (loadGroupstmp.filter(el => el.groupIndex === groupIndex).length > 0)
        loadGroupstmp.filter(el => el.groupIndex === groupIndex)[0].items.push(fields[i])
      else
        loadGroupstmp.push({ groupIndex: groupIndex, items: [fields[i]] })
    }

    setGroupOptions(groupOptionstmp)
    setLoadGroups(loadGroupstmp)
  }

  const setExpandedRows = () => {
    const expand = []

    for (let i = 0; i < loadGroups.length; i++)
      expand.push(i)

    setExpandRows(expand)
  }

  const addItemToList = async (data: any, showSuccessMessage: boolean = true) => {
    setCircularLoading(true);

    //Empresa: se mais de uma, o usuario deve ter escolhido no campo.
    if (shed.companies.length > 1)
      data.isCompanyRequired = true;
    else
      data.shippingCompany = { code: shed.companies[0].companyId, label: shed.companies[0].companyName, uf: shed.companies[0].companyUF, companyCode: shed.companies[0].companyCode };

    //Verificar se embalagem eh FCM, para validacao
    data.isRackFCM = false
    if (data.rackType?.rackTypeCode && data.shippingCompany?.code)
      await MvmApi.isPackingFCM(data.rackType.rackTypeCode, data.shippingCompany.code)
        .then(response => {
          data.isRackFCM = response
          if (!response) data.typeReqNumberInGrid = false;
        });

    if (!(await yupValidation(data))) {
      setCircularLoading(false);
      return;
    }

    data.mvmId = 0;
    data.supplierId = data.supplier.code;
    data.supplierName = data.supplier.name;
    data.supplierUF = data.supplier.supplierUf;
    data.supplierSapCode = data.supplier.sapCode;
    data.rackTypeId = data.rackType.rackTypeId;
    data.rackTypeCode = data.rackType.rackTypeCode;
    data.rackTypeDescription = data.rackType.rackTypeDescription;
    data.rackTypeOwnerSupplierId = data.rackType.ownerSupplierId;
    data.shedId = shedId;
    data.shedCode = shed.code;
    data.movementTypeId = movementTypeId;
    data.shedMovementTypeDescription = movementTypeDescription;
    data.shippingCompanyId = data.shippingCompany.code;
    data.shippingCompanyCode = data.shippingCompany.companyCode;
    data.shippingCompanyName = data.shippingCompany.label;
    data.shippingCompanyUF = data.shippingCompany.uf;
    data.loadType = "R";
    data.hasError = false;

    if (+user.company.plantID === 1 && data.requestNumber !== '')
      data.invoiceCFOP = 9999;

    proccessItemGroupInfo(data, data.groupDrop?.code)

    // item ja esta na lista? add verificação se Ordem de Transporte está preenchida // Rodrigo
    let existingItem = false;
    fields.forEach((field: any) => {
      if (field.requestNumber === data.requestNumber
        && field.rackTypeId === data.rackTypeId
        && field.supplierId === data.supplierId
        && data.requestNumber?.length > 0) {
        existingItem = true;
      }
    })

    if (existingItem) {
      enqueueSnackbar(it("itemAlreadyOnList"), { variant: 'warning' })
      setCircularLoading(false)
      user.company.companyID === '167' ? refInvoiceCFOPField.current?.focus() : refRackTypeField.current?.focus();
      return;
    }

    //Adicionar na lista:
    append(data);
    if (showSuccessMessage) enqueueSnackbar(it("mvmRackLoadAddSuccess"), { variant: 'success' });

    //Resetar campos, mas manter estes 2:
    reset({
      supplier: data.supplier,
      shippingCompany: data.shippingCompany,
      groupDrop: { code: data.invoiceGroup, label: `Grupo: ${data.invoiceGroup}` }
    })

    user.company.companyID === '167' ? refInvoiceCFOPField.current?.focus() : refRackTypeField.current?.focus();

    setCircularLoading(false);
  }

  useEffect(() => {
    proccessGroupList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields])

  useEffect(() => {
    setExpandedRows()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadGroups])

  const onSubmit = (data: any) => { }

  const removeItemFromList = (index: number) => {
    remove(index)
  }

  const expandableRowsOptions: any = {

    expandableRows: true,
    expandableRowsHeader: false,
    expandableRowsOnClick: true,
    rowsExpanded: expandRows,
    renderExpandableRow: (rowData: any, rowMeta: any) => {
      return (
        <Grid style={{ width: "200%", marginTop: 5, marginLeft: 10, marginBottom: 10 }}>
          <MuiThemeProvider theme={myTheme}  >
            <MUIDataTable
              title=""
              data={loadGroups.filter(f => f.groupIndex === rowData[0]).length > 0 ? loadGroups.filter(f => f.groupIndex === rowData[0])[0].items : []}
              columns={columns}
              options={{

                ...getDefaultGridOptions(_t),
                serverSide: false,
                pagination: false,
                sort: false,
                selectToolbarPlacement: "none",
                filter: false,
                search: false,
                download: false,
                print: false,
                viewColumns: false,
                selectableRows: 'none',
              }}
            />
          </MuiThemeProvider>
        </Grid>

      );
    }
  }

  return (
    <Grid style={{ marginTop: 20, width: "98%", marginLeft: 10, marginBottom: 10 }}>
      <Typography className={classes.root} style={{ marginLeft: 0, marginBottom: 10 }} >
        {title ?? t("outgoingRequests")}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>

        {!allowAdd ? <></> :
          <Grid container spacing={1} style={{ marginLeft: -12, marginBottom: 10 }}>
            {isJeep() && (
              <Grid item xs={2} >
                <Select
                  inputRef={refInvoiceCFOPField}
                  label={it("InvoiceCFOP")}
                  name="selectedCFOP"
                  control={formControl}
                  options={MVM_RACKLOAD_TYPE}
                  style={{ width: '100%' }}
                  required={true}

                />
              </Grid>
            )}

            {COMPANIES_WITH_FCM_RACKS.indexOf(Number(user.company.companyID)) >= 0 && ( //Campo Ordem de transporte somente visivel se a empresa utilizar embalagens FCM. E somente obrigatorio se a embalagem escolhida for FCM
              <Grid item xs={1} >
                <TextField
                  label={user.company.companyID === '167' ? it("RequestNumber164") : it("transportOrder")}
                  name="requestNumber"
                  control={formControl}
                  inputProps={{ maxLength: 12 }}
                  fullWidth
                  disable={(+user.company.plantID === 3 && watchInvoiceCFOP?.code !== '9999') ? true : false}
                  required={watchInvoiceCFOP?.code === '9999'} />
              </Grid>
            )}

            {isFiasa() && (
              <Grid item xs={1} style={{ marginLeft: '25px', marginTop: '8px', whiteSpace: 'nowrap' }}>
                <CheckboxUI
                  label={it('indFullRack')}
                  name="indFullRack"
                  control={formControl} />
              </Grid>
            )}

            <Grid item xs={2} >
              <SelectRackType
                label={it('rackType')}
                name="rackType"
                control={formControl}
                inputRef={refRackTypeField}
                multiple={false} />
            </Grid>

            <Grid item xs={2} >
              <SelectSupplier name="supplier" control={formControl} />
            </Grid>

            <Grid item xs={1} >
              <TextField
                label={it("rackTypeQuantity")}
                name="rackTypeQuantity"
                control={formControl}
                type="number"
                fullWidth />
            </Grid>
            <Grid item xs={1} >
              <TextField
                label={it("rackTypeVolume")}
                name="volumeQuantity"
                control={formControl}
                type="number"
                fullWidth />
            </Grid>

            <Grid item xs={1}>
              <Select
                label={it("shippingCompanyId")}
                name="shippingCompany"
                control={formControl}
                options={shedCompanyList}
                style={{ width: '100%' }}
                required
              />
            </Grid>
            {groupOptions.length > 1 && user.company.companyID === '167' &&
              <Grid item xs={1}>
                <Select
                  label={'Grupo NFe'}
                  name={"groupDrop"}
                  control={formControl}
                  options={groupOptions}
                  style={{ width: '100%' }} />
              </Grid>}
            <ButtonUI
              color="primary"
              onClick={() => addItemToList(getValues())}
              variant="contained"
              style={{
                maxWidth: '40px',
                maxHeight: '38px',
                minWidth: '40px',
                minHeight: '38px',
                marginTop: 22,
                marginLeft: '15px'
              }}>
              <AddIcon />
            </ButtonUI>
          </Grid>
        }
      </form>

      <Backdrop style={{ zIndex: 9999 }} open={circularLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Typography className={classes.paper} style={{ marginLeft: 0 }} >
        {_t("totalRecords")} {loadGroups ? loadGroups.length : '0'}
      </Typography>
      <MuiThemeProvider theme={myTheme} >
        <MUIDataTable
          title=""
          data={loadGroups}
          columns={columnsGroup}
          options={{
            ...getDefaultGridOptions(_t),
            serverSide: false,
            pagination: false,
            sort: false,
            selectToolbarPlacement: "none",
            filter: false,
            search: false,
            download: false,
            print: false,
            viewColumns: false,
            selectableRows: 'none',
            ...expandableRowsOptions
          }}
        />
      </MuiThemeProvider>

    </Grid>
  )
}

export default MvmRackLoadList